import { Button, Checkbox, Chip, Group, MultiSelect, Stack, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import type { SearchQuery } from "../../api/hooks";
import { useCompanyOptions, useNames } from "../../api/hooks";
import type { SearchResponse } from "../../api/types";
import { clearedFilters, hasActiveFilters } from "./url-state";

interface Props {
  value: SearchQuery;
  facets: SearchResponse["facets"] | undefined;
  onChange(next: SearchQuery): void;
}

export function SearchFilters({ value, facets, onChange }: Props) {
  const { t } = useTranslation();
  const { lookups, docType } = useNames();
  const count = (list: { id: number | null; count: number }[] | undefined, id: number) =>
    list?.find((f) => f.id === id)?.count;

  const companyOptions = useCompanyOptions(value.company).map((o) => {
    const n = count(facets?.companies, Number(o.value));
    return n !== undefined ? { ...o, label: `${o.label} (${n})` } : o;
  });
  const typeOptions = (lookups.data?.docTypes ?? []).map((d) => {
    const n = count(facets?.types, d.id);
    return {
      value: String(d.id),
      label: n !== undefined ? `${docType(d.id)} (${n})` : docType(d.id),
    };
  });
  const years = (facets?.years ?? []).filter((y) => y.year !== null).slice(0, 8);
  const selectedYear =
    value.from &&
    value.to &&
    value.from.slice(5) === "01-01" &&
    value.to.slice(5) === "12-31" &&
    value.from.slice(0, 4) === value.to.slice(0, 4)
      ? value.from.slice(0, 4)
      : null;
  const hasFilters = hasActiveFilters(value);

  return (
    <Stack gap="sm">
      <MultiSelect
        label={t("search.filters.company")}
        data={companyOptions}
        value={value.company.map(String)}
        onChange={(v) => onChange({ ...value, company: v.map(Number), page: 1 })}
        searchable
        clearable
        nothingFoundMessage={t("common.nothing")}
      />
      <MultiSelect
        label={t("search.filters.type")}
        data={typeOptions}
        value={value.type.map(String)}
        onChange={(v) => onChange({ ...value, type: v.map(Number), page: 1 })}
        searchable
        clearable
        nothingFoundMessage={t("common.nothing")}
      />
      <Group grow>
        <DateInput
          label={t("search.filters.from")}
          value={value.from}
          onChange={(v) => onChange({ ...value, from: v, page: 1 })}
          valueFormat="DD.MM.YYYY"
          clearable
        />
        <DateInput
          label={t("search.filters.to")}
          value={value.to}
          onChange={(v) => onChange({ ...value, to: v, page: 1 })}
          valueFormat="DD.MM.YYYY"
          clearable
        />
      </Group>
      {years.length > 0 && (
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            {t("search.filters.years")}
          </Text>
          <Chip.Group
            multiple={false}
            value={selectedYear}
            onChange={(y) => {
              const year = typeof y === "string" ? y : null;
              onChange(
                year && year !== selectedYear
                  ? { ...value, from: `${year}-01-01`, to: `${year}-12-31`, page: 1 }
                  : { ...value, from: null, to: null, page: 1 },
              );
            }}
          >
            <Group gap={6}>
              {years.map((y) => (
                <Chip key={y.year} value={String(y.year)} size="xs">
                  {y.year} ({y.count})
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </Stack>
      )}
      <Checkbox
        label={t("search.filters.superseded")}
        checked={value.superseded}
        onChange={(e) => onChange({ ...value, superseded: e.currentTarget.checked, page: 1 })}
      />
      {hasFilters && (
        <Button variant="subtle" size="compact-sm" onClick={() => onChange(clearedFilters(value))}>
          {t("search.filters.clear")}
        </Button>
      )}
    </Stack>
  );
}
